import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, Bell, Globe, ShieldCheck, LogOut, Edit2, BellRing, Loader2, Send, Trash2, Plus, Wallet, Ticket, Type } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { CURRENCIES } from '../mock';
import push from '../services/push';
import api from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useFontScale } from '../hooks/useFontScale';
import { toast } from 'sonner';

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const { currency, setCurrency, currencyOptions } = useApp();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp_number || '');
  const [city, setCity] = useState(user?.city || '');
  const [country, setCountry] = useState(user?.country || '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notif, setNotif] = useState({ promos: true, orders: true, news: false });

  // Address book
  const [addresses, setAddresses] = useState([]);
  const [addrForm, setAddrForm] = useState(null); // null | { id?, label, address, lat, lng, is_default }
  const [wallet, setWallet] = useState({ balance: 0, currency: 'CAD' });
  const [ticketCount, setTicketCount] = useState(0);
  const [redeemCode, setRedeemCode] = useState('');

  // Web-push state
  const [pushSupported] = useState(() => push.supported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const { scale: fontScale, setScale: setFontScale, options: fontScaleOptions } = useFontScale();
  const [pushBusy, setPushBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [me, addrs, w, r] = await Promise.all([
        api.get('/me').then((r) => r.data),
        api.get('/me/addresses').then((r) => r.data.addresses || []),
        api.get('/me/wallet').then((r) => r.data),
        api.get('/me/raffle-tickets').then((r) => r.data),
      ]);
      setName(me?.name || ''); setEmail(me?.email || '');
      setPhone(me?.phone || ''); setCity(me?.city || ''); setCountry(me?.country || '');
      setWhatsapp(me?.whatsapp_number || '');
      setAddresses(addrs);
      setWallet({ balance: w?.balance || 0, currency: w?.currency || 'CAD' });
      setTicketCount(r?.count || 0);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAll(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/me', { name, email, phone, city, country, whatsapp_number: whatsapp });
      setEditing(false);
      toast.success('Profile updated');
      refreshUser?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const saveAddress = async () => {
    if (!addrForm) return;
    if (!addrForm.address || addrForm.address.trim().length < 3) {
      toast.error('Please enter an address'); return;
    }
    try {
      if (addrForm.id) {
        await api.patch(`/me/addresses/${addrForm.id}`, {
          label: addrForm.label, address: addrForm.address,
          lat: addrForm.lat, lng: addrForm.lng,
          is_default: !!addrForm.is_default,
        });
        toast.success('Address updated');
      } else {
        await api.post('/me/addresses', {
          label: addrForm.label, address: addrForm.address,
          lat: addrForm.lat, lng: addrForm.lng,
          is_default: !!addrForm.is_default,
        });
        toast.success('Address saved');
      }
      setAddrForm(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await api.delete(`/me/addresses/${id}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const redeemGiftCard = async () => {
    const code = (redeemCode || '').trim();
    if (!code) return;
    try {
      const { data } = await api.post('/me/gift-cards/redeem', { code });
      toast.success(data.message || 'Redeemed');
      setRedeemCode('');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid code'); }
  };

  const topUpWallet = async () => {
    const raw = window.prompt('Top up amount (CAD):', '20');
    if (!raw) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Invalid amount'); return; }
    try {
      const { data } = await api.post('/me/wallet/topup', { amount, currency: 'CAD' });
      toast.success(`Wallet: ${data.balance} ${data.currency}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Top up failed'); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!push.supported()) return;
      const sub = await push.currentSubscription();
      if (alive) setPushEnabled(!!sub && push.permission() === 'granted');
    })();
    return () => { alive = false; };
  }, []);

  const togglePush = async (checked) => {
    setPushBusy(true);
    try {
      if (checked) {
        await push.enable();
        setPushEnabled(true);
        toast.success("You'll receive order updates even when the app is closed.");
      } else {
        await push.disable();
        setPushEnabled(false);
        toast.success('Push notifications turned off.');
      }
    } catch (e) {
      toast.error(e.message || 'Could not update notification preference.');
    } finally { setPushBusy(false); }
  };

  const sendTestPush = async () => {
    setPushBusy(true);
    try {
      const r = await push.sendTest();
      if (!r?.delivered) toast.error('No devices are subscribed yet.');
      else toast.success(`Test sent to ${r.delivered} device${r.delivered === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed.');
    } finally { setPushBusy(false); }
  };

  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Account</h1>
        <p className="text-slate-500 mt-1">Manage your profile, addresses, and preferences.</p>

        {/* Profile card */}
        <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            {user?.avatar ? (
              <img src={user.avatar} alt="avatar" className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <div className="h-16 w-16 rounded-full bg-slate-900 text-white flex items-center justify-center text-xl font-semibold">{initials}</div>
            )}
            <div className="flex-1">
              <div className="font-semibold text-slate-900 text-lg">{user?.name}</div>
              <div className="text-slate-500 text-sm">{user?.email}</div>
              <div className="text-xs text-slate-400 mt-1">Signed in with {user?.provider === 'google' ? 'Google' : 'Email'}</div>
            </div>
            <Button variant="outline" onClick={() => setEditing((e) => !e)}>
              <Edit2 className="h-4 w-4 mr-1.5" /> {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>
          {editing && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-700">Full name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-10" data-testid="profile-name" />
              </div>
              <div>
                <label className="text-sm text-slate-700">Email</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-10" data-testid="profile-email" />
              </div>
              <div>
                <label className="text-sm text-slate-700">Phone (WhatsApp)</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-10" placeholder="+14165550123" data-testid="profile-phone" />
              </div>
              <div>
                <label className="text-sm text-slate-700">WhatsApp number</label>
                <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className="mt-1.5 h-10" placeholder="+14165550123" data-testid="profile-whatsapp" />
                <p className="text-[11px] text-slate-400 mt-1">Get order updates on WhatsApp. Leave blank to receive SMS instead.</p>
              </div>
              <div>
                <label className="text-sm text-slate-700">City</label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5 h-10" data-testid="profile-city" />
              </div>
              <div>
                <label className="text-sm text-slate-700">Country</label>
                <Input value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1.5 h-10" placeholder="CA" data-testid="profile-country" />
              </div>
              <div className="sm:col-span-2">
                <Button onClick={saveProfile} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save">
                  {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : 'Save changes'}
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Wallet */}
        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6" data-testid="wallet-section">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Platform wallet</h2>
            <span className="ml-auto text-2xl font-bold text-slate-900" data-testid="wallet-balance">
              {wallet.balance.toFixed(2)} <span className="text-xs font-normal text-slate-500">{wallet.currency}</span>
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Wallet funds can be used to pay for delivery fees at checkout. Raffle winnings and gift cards land here.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={topUpWallet} data-testid="wallet-topup"><Plus className="h-3 w-3 mr-1" /> Top up</Button>
            <Input placeholder="Gift card code" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} className="max-w-64 h-9" data-testid="giftcard-input" />
            <Button size="sm" onClick={redeemGiftCard} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="giftcard-redeem">Redeem</Button>
            <div className="ml-auto inline-flex items-center gap-1 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="raffle-tickets-badge">
              <Ticket className="h-3 w-3" /> {ticketCount} raffle ticket{ticketCount === 1 ? '' : 's'}
            </div>
          </div>
        </section>

        {/* Addresses */}
        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6" data-testid="addresses-section">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Saved addresses</h2>
          </div>
          <div className="mt-3 space-y-2">
            {addresses.length === 0 && !addrForm && (
              <div className="text-sm text-slate-400 text-center py-4">
                No saved addresses yet.
              </div>
            )}
            {addresses.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200" data-testid={`address-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">{a.label}</span>
                    {a.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Default</span>}
                  </div>
                  <div className="text-sm text-slate-500 truncate">{a.address}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setAddrForm({ ...a })} data-testid={`address-edit-${a.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteAddress(a.id)} data-testid={`address-delete-${a.id}`}>
                    <Trash2 className="h-3 w-3 text-rose-600" />
                  </Button>
                </div>
              </div>
            ))}
            {addrForm ? (
              <div className="p-3 rounded-xl border border-emerald-300 bg-emerald-50/40 space-y-2" data-testid="address-form">
                <Input placeholder="Label (Home, Work…)" value={addrForm.label || ''} onChange={(e) => setAddrForm((f) => ({ ...f, label: e.target.value }))} data-testid="address-label" />
                <AddressAutocomplete
                  value={addrForm.address || ''}
                  onChange={(addr, geo) => {
                    if (geo?.lat != null && geo?.lng != null) {
                      setAddrForm((f) => ({ ...f, address: addr, lat: geo.lat, lng: geo.lng }));
                    } else {
                      setAddrForm((f) => ({ ...f, address: addr }));
                    }
                  }}
                  placeholder="Street address"
                  data-testid="address-input"
                />
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!addrForm.is_default} onChange={(e) => setAddrForm((f) => ({ ...f, is_default: e.target.checked }))} data-testid="address-default" />
                  Set as default
                </label>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveAddress} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="address-save">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAddrForm(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="w-full mt-1" onClick={() => setAddrForm({ label: 'Home', address: '', is_default: false })} data-testid="address-add">
                <Plus className="h-4 w-4 mr-1" /> Add new address
              </Button>
            )}
          </div>
        </section>

        {/* Payment */}
        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Payment methods</h2>
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="h-9 w-12 rounded bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">VISA</div>
                <div>
                  <div className="font-medium text-slate-900">Visa •••• 4242</div>
                  <div className="text-sm text-slate-500">Expires 09/27</div>
                </div>
              </div>
              <Button variant="ghost" size="sm">Remove</Button>
            </div>
            <Button variant="outline" className="w-full">+ Add payment method</Button>
          </div>
        </section>

        {/* Preferences */}
        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Preferences</h2>
          </div>
          <div className="mt-3">
            <label className="text-sm text-slate-700">Currency</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {(currencyOptions || CURRENCIES).map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCurrency(c)}
                  className={`px-3 h-9 rounded-full text-sm border ${currency.code === c.code ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                >
                  {c.symbol} {c.code}
                </button>
              ))}
            </div>
          </div>

          {/* Font-scale accessibility toggle */}
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="font-scale-section">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-emerald-600" />
              <label className="text-sm font-medium text-slate-800">Larger text</label>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Bump the UI back up if the default feels too tight on your device.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {fontScaleOptions.map((s) => (
                <button
                  key={s.value}
                  onClick={() => { setFontScale(s.value); toast.success(`Text size: ${s.label}`); }}
                  data-testid={`font-scale-${s.value}`}
                  className={`px-3 h-9 rounded-full border transition-colors ${fontScale === s.value ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                  style={{ fontSize: `${s.px}px` }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Stored on this device only — set it separately on each phone or browser.</p>
          </div>
        </section>

        {/* Notifications */}
        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6" data-testid="notifications-section">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Notifications</h2>
          </div>

          {/* Web-push toggle — the real integration */}
          <div className="mt-3 p-3 rounded-xl border border-emerald-100 bg-emerald-50/40 flex items-start gap-3" data-testid="push-toggle-card">
            <div className="mt-0.5 h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <BellRing className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">Push notifications</div>
              <div className="text-sm text-slate-600">
                {pushSupported
                  ? 'Get order updates on this device even when ShipitNow is closed.'
                  : 'This browser does not support push notifications.'}
              </div>
              {pushSupported && pushEnabled && (
                <button onClick={sendTestPush} disabled={pushBusy}
                  data-testid="push-test-btn"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                  {pushBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Send a test notification
                </button>
              )}
            </div>
            <Switch
              checked={pushEnabled}
              disabled={!pushSupported || pushBusy}
              onCheckedChange={togglePush}
              data-testid="push-toggle"
            />
          </div>

          <div className="mt-3 divide-y divide-slate-100">
            <Toggle label="Order updates" sub="Get notified about your active orders" v={notif.orders} onChange={(v) => setNotif({ ...notif, orders: v })} />
            <Toggle label="Promotions & offers" sub="Discounts, deals, and seasonal offers" v={notif.promos} onChange={(v) => setNotif({ ...notif, promos: v })} />
            <Toggle label="Newsletter" sub="Monthly product updates" v={notif.news} onChange={(v) => setNotif({ ...notif, news: v })} />
          </div>
        </section>

        <section className="mt-5 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-slate-900 mb-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <span className="font-semibold">Security</span>
          </div>
          <Button variant="outline" className="w-full sm:w-auto">Change password</Button>
        </section>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={() => { logout(); nav('/login'); }} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </main>
    </div>
  );
};

const Toggle = ({ label, sub, v, onChange }) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <div className="font-medium text-slate-900">{label}</div>
      <div className="text-sm text-slate-500">{sub}</div>
    </div>
    <Switch checked={v} onCheckedChange={onChange} />
  </div>
);

export default Profile;
